<template lang="pug">
section.start
  .start__bg
    IllustrationTop.start__bg-top
    .start__space#star-field
    IllustrationBottom.start__bg-bottom

  .start__container
    .lp-headline-text.start__text
      h1.lp-headline-title(v-html="$t('pages.ssr_landing.start_screen.title')")
      .lp-headline-unlimited
        span.lp-headline-unlimited-text {{ $t('pages.ssr_landing.start_screen.unlimited_episodes') }}
        .lp-headline-unlimited-separator
        span.lp-headline-unlimited-text {{ $t('pages.ssr_landing.start_screen.unlimited_storage') }}
      p.lp-headline-description {{ $t('pages.ssr_landing.start_screen.description') }}
      .start__buttons
        RssUiButton.start__text-button.lp-button.link-button-arrow-animation(
          :data-test-id="buttonDataTestId"
          :href="buttonHref"
          :blank="false"
          rainbow
        )
          template {{ buttonText }}
          .link-button-arrow-animation__i
            SmallArrowIcon
      img.start__image(
        src="/images/dashboard.png"
        srcset="/images/dashboard@2x.png 2x"
        alt="Dashboard mockup"
      )
</template>

<script>
import { mapState } from 'vuex'

import IllustrationTop from './svg/IllustrationTop'
import IllustrationBottom from './svg/IllustrationBottom'

import SmallArrowIcon from '@/components/icons/default/ArrowSmall'

import { config } from '@/config'

export default {
  components: {
    SmallArrowIcon,
    IllustrationTop,
    IllustrationBottom
  },

  computed: {
    ...mapState('auth', ['isLoggedIn']),

    dashboardAppUrl () {
      return config.dashboardAppUrl
    },

    buttonDataTestId () {
      if (!this.isLoggedIn) {
        return 'buttonSignupCTA'
      }

      return undefined
    },

    buttonText () {
      if (!this.isLoggedIn) {
        return this.$t('pages.ssr_landing.start_screen.button')
      }
      return this.$t('homepage.hero.button_logged')
    },

    buttonHref () {
      if (!this.isLoggedIn) {
        return this.$locatedLink('/auth/sign-up/', this.dashboardAppUrl)
      }
      return this.$locatedLink('/', this.dashboardAppUrl)
    }
  },

  mounted () {
    const $starField = document.getElementById('star-field')
    const $backIllustrationLayer = document.querySelector('.start')

    const numberOfStars = 18
    const starAnimationSpeed = 0.1
    if ($starField) {
      const starFieldWidth = $starField.offsetWidth
      const starFieldHeight = $backIllustrationLayer.offsetHeight * 0.8

      this.addStars(starFieldWidth, starFieldHeight, numberOfStars)
      this.animateStars(starFieldWidth, starAnimationSpeed)
    }
  },

  methods: {
    addStars (starFieldWidth, starFieldHeight, noOfStars) {
      const starField = document.getElementById('star-field')
      const numberOfStars = noOfStars

      for (let i = 0; i < numberOfStars; i++) {
        const star = document.createElement('div')
        star.className = 'star'
        const topOffset = Math.floor((Math.random() * starFieldHeight) + 1)
        const leftOffset = Math.floor((Math.random() * starFieldWidth) + 1)
        star.style.top = topOffset + 'px'
        star.style.left = leftOffset + 'px'
        star.style.position = 'absolute'
        starField.appendChild(star)
      }
    },

    animateStars (starFieldWidth, speed) {
      const starField = document.getElementById('star-field')
      const stars = starField.childNodes

      function getStarColor (index) {
        if (index % 8 === 0) {
          return 'red'
        } else if (index % 10 === 0) {
          return 'yellow'
        } else if (index % 17 === 0) {
          return 'blue'
        } else {
          return 'white'
        }
      }

      function getStarDistance (index) {
        if (index % 6 === 0) {
          return ''
        } else if (index % 9 === 0) {
          return 'near'
        } else if (index % 2 === 0) {
          return 'far'
        } else {
          return 0
        }
      }

      function getStarRelativeSpeed (index) {
        if (index % 6 === 0) {
          return 0.1
        } else if (index % 9 === 0) {
          return 0.4
        } else if (index % 2 === 0) {
          return 0.2
        } else {
          return 0.1
        }
      }

      setInterval(function () {
        for (let i = 0; i < stars.length; i++) {
          stars[i].className = 'star' + ' ' + getStarColor(i) + ' ' + getStarDistance(i)

          const currentLeft = parseInt(stars[i].style.left, 10)
          const leftChangeAmount = speed + getStarRelativeSpeed(i)
          let leftDiff

          if (currentLeft - leftChangeAmount < 0) {
            leftDiff = currentLeft - leftChangeAmount + starFieldWidth
          } else {
            leftDiff = currentLeft - leftChangeAmount
          }
          stars[i].style.left = leftDiff + 'px'
        }
      }, 20)
    },

    switchToRss () {
      this.$router.push(this.$locatedLink('/switch-to-rss/'))
    }
  }
}
</script>

<style lang="scss" scoped>
.lp-headline {
  &-subtitle {
    font-size: 16px;
  }

  &-title {
    width: 80%;
    text-align: center;
    font-size: 90px;
    font-weight: 600;
    margin-bottom: 0;

    @include display-less(hd) {
      width: 100%;
    }

    @include display-less(desktop) {
      font-size: 76px;
    }

    @include display-less(tablet) {
      font-size: 60px;
    }

    @include display-less(phablet) {
      font-size: 48px;
    }

    /deep/ strong {
      font-style: italic;
      background: linear-gradient(131deg, #F87900 -0.63%, #FE4F45 58%, #E419BA 114.33%);
      background-clip: text;
      color: transparent;
      -webkit-text-fill-color: transparent;
    }
  }

  &-description {
    width: 80%;
    font-size: 24px;
    text-align: center;
    margin-bottom: $gap * 2.5;

    @include display-less(phablet) {
      font-size: 18px;
    }
  }
}

.lp-headline-unlimited {
  display: flex;
  align-items: center;
  gap: $gap * 2;
  margin: $gap * 1.5 0;

  @include display-less(phablet) {
    flex-direction: column;
    gap: $gap * 0.5;
    margin: $gap 0;
  }

  &-separator {
    width: 1px;
    height: 38px;
    background: #fff;

    @include display-less(phablet) {
      display: none;
    }
  }

  &-text {
    font-size: 24px;
    color: #fff;
    text-transform: uppercase;
    font-weight: 700;
  }
}

.start {
  width: 100%;
  height: auto;
  position: relative;
  background: #1C0C32;

  @include display-less(tablet) {
    height: auto;
    display: flex;
    flex-direction: column-reverse;
  }

  &__container {
    width: 100%;
    margin: 0 auto;
    max-width: 1498px;
  }

  &__space {
    top: 0;
    left: 0;
    z-index: 3;
    width: 100%;
    height: 100%;
    position: absolute;
  }

  &__text-button {
    @include display-less(smart) {
      /deep/ .rss-ui-button__text {
        font-size: 15px;
      }
    }
  }

  &__bg {
    overflow: hidden;
    width: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    width: 100%;
    display: flex;
    margin-top: $gap;
    position: relative;
    margin-top: -$gap * 0.5;
    justify-content: center;
    position: absolute;

    &-top {
      width: 100%;
      height: auto;
      position: absolute;
      top: 0;
      left: 0;
    }

    &-bottom {
      width: 100%;
      height: auto;
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }

  &__text {
    z-index: 2;
    width: 100%;
    display: flex;
    position: relative;
    align-items: center;
    flex-direction: column;
    padding-top: $gap * 2;

    @media (min-width: 1562px) {
      width: 100%;
      padding-left: 0;
      padding-right: 0;
    }

    @include display-less(big) {
      padding-left: $gap * 2;
      padding-right: $gap * 2;
      width: 100%;
    }

    @include display-less(desktop) {
      width: 100%;
      padding-top: $gap * 6;
    }

    @include display-less(tablet) {
      width: 100%;
      position: relative;
      padding-left: $gap;
      padding-right: $gap;
      padding-top: 1.5 * $gap;
    }

    @include display-less(phablet) {
      padding-bottom: $gap * 3;
    }

    &-card {
      color: #fff;
      font-size: 14px;
      font-weight: 500;
      line-height: 160%;
      margin-top: $gap * 0.5;

      @include display-less(tablet) {
        width: 100%;
        text-align: center;
      }

      @include display(max) {
        font-size: 18px;
      }
    }

    &-button {
      @include display-less(phablet) {
        width: 100%;
      }

      @include display(max) {
        :deep(.rss-ui-button) {
          height: 60px;
          font-size: 22px;
          min-height: 60px;
          line-height: 60px;
        }
      }
    }
  }

  &__buttons {
    width: 100%;
    display: flex;
    gap: $gap;
    align-items: center;
    justify-content: center;

    @include display-less(phablet) {
      flex-direction: column;
      gap: $gap;
    }
  }

  &__image {
    width: 100%;
    height: auto;
    max-width: 1200px;
    margin-top: $gap * 3;
  }
}

$starDimension: 2px;

.start__space {
  ::v-deep .star {
    width: $starDimension;
    height: $starDimension;
    position: absolute;
    background: #fff;
    border-radius: 50%;

    &.far {
      width: $starDimension - 1;
      height: $starDimension - 1;
    }

    &.near {
      width: $starDimension + 2;
      height: $starDimension + 2;
    }

    &.white {
      background: #fff;
      box-shadow: 0 0 15px #fff;
    }

    &.yellow {
      background: #BFBB40;
      box-shadow: 0 0 15px #BFBB40;
    }

    &.blue {
      background: #4444ff;
      box-shadow: 0 0 15px #4444ff;
    }

    &.red {
      background: #ff4444;
      box-shadow: 0 0 15px #ff4444;
    }
  }

  @include display-less(tablet) {
    display: none;
  }
}

.link-button-arrow-animation {
  @include display(max) {
    .link-button-arrow-animation__i {
      width: 20px;
      height: 20px;

      ::v-deep svg {
        width: 100%;
        height: auto;
      }

      &:after {
        right: 4px;
        height: 4px;
        margin-top: -2px;
      }
    }
    &:hover {
      .link-button-arrow-animation__i {
        &:after {
          width: 14px;
        }
      }
    }
  }
}
</style>
